import { ComponentType, lazy, ReactNode, Suspense } from 'react';
import { Navigate, redirect } from 'react-router-dom';

import { TemplateType } from '@e-origin/shared';

import { RouteErrorBoundary } from '@components';

// All admin components are lazy-loaded so they compile into a separate chunk and
// are only fetched when the admin routes are actually rendered (admin host).
// The public app never registers these routes, so the chunk is never requested.
const lazyNamed = <P = Record<string, never>,>(factory: () => Promise<Record<string, unknown>>, name: string) =>
  lazy(async () => ({ default: (await factory())[name] as ComponentType<P> }));

const AdminLogin = lazyNamed(() => import('./components/auth/AdminLogin'), 'AdminLogin');
const AdminProtectedRoute = lazyNamed<{ children?: ReactNode }>(() => import('./components/auth/AdminProtectedRoute'), 'AdminProtectedRoute');
const AdminUserLayout = lazyNamed(() => import('./components/layout/UserLayout'), 'AdminUserLayout');
const CustomsSystems = lazyNamed(() => import('./components/CustomsSystems'), 'CustomsSystems');
const ManageBatches = lazyNamed(() => import('./components/ManageBatches'), 'ManageBatches');
const ManageCatalogs = lazyNamed(() => import('./components/ManageCatalogs'), 'ManageCatalogs');
const ManageCharts = lazyNamed(() => import('./components/ManageCharts'), 'ManageCharts');
const ManageCodes = lazyNamed(() => import('./components/ManageCodes'), 'ManageCodes');
const ManageCodesFamily = lazyNamed(() => import('./components/ManageCodes'), 'ManageCodesFamily');
const ManageCustomsErrors = lazyNamed(() => import('./components/ManageCustomsErrors'), 'ManageCustomsErrors');
const HsCodeClassifier = lazyNamed(() => import('./components/HsCodeClassifier'), 'HsCodeClassifier');
const ManageDeclarants = lazyNamed(() => import('./components/ManageDeclarants'), 'ManageDeclarants');
const EditDeclarant = lazyNamed(() => import('./components/ManageDeclarants'), 'EditDeclarant');
const CompareVatReports = lazyNamed(() => import('./components/ManageDeclarants'), 'CompareVatReports');
const ManageTemplates = lazyNamed(() => import('./components/ManageTemplates'), 'ManageTemplates');
const EditTemplate = lazyNamed(() => import('./components/ManageTemplates'), 'EditTemplate');
const ManageTransactionTypes = lazyNamed(() => import('./components/ManageTransactionTypes'), 'ManageTransactionTypes');
const ManageTransits = lazyNamed(() => import('./components/ManageTransits'), 'ManageTransits');
const ManageTSDs = lazyNamed(() => import('@admin/components/ManageTSDs'), 'ManageTSDs');
const ManageUsers = lazyNamed(() => import('./components/ManageUsers'), 'ManageUsers');
const RequestLogger = lazyNamed(() => import('./components/RequestLogger'), 'RequestLogger');

const AdminFallback = () => <div style={{ padding: 24 }}>Loading…</div>;

const suspend = (node: ReactNode) => <Suspense fallback={<AdminFallback />}>{node}</Suspense>;

// Build the admin route tree at an arbitrary base path. On a dedicated admin host the
// app is mounted at root (base = ''), so URLs are e.g. /manage-users with no /admin
// prefix; elsewhere it is mounted under /admin.
export const buildAdminRoutes = (base = '/admin') => {
  const editTemplateLoader = ({ params }) => {
    const { type, id } = params;

    if (!Object.values(TemplateType).includes(type)) {
      return redirect(`${base}/manage-templates`);
    }

    if (id && !/^[a-f\d]{24}$/i.test(id)) {
      return redirect(`${base}/manage-templates`);
    }

    return null;
  };

  return [
    {
      path: base || '/',
      errorElement: <RouteErrorBoundary />,
      children: [
        {
          path: '',
          element: suspend(
            <AdminProtectedRoute>
              <AdminUserLayout />
            </AdminProtectedRoute>,
          ),
          children: [
            { index: true, element: <Navigate to="manage-users" replace /> },
            { path: 'manage-users', element: suspend(<ManageUsers />) },
            { path: 'manage-declarants', element: suspend(<ManageDeclarants />) },
            { path: 'manage-declarants/:id', element: suspend(<EditDeclarant />) },
            { path: 'manage-declarants/:id/compare-vat-reports', element: suspend(<CompareVatReports />) },
            { path: 'manage-transaction-types', element: suspend(<ManageTransactionTypes />) },
            { path: 'manage-templates', element: suspend(<ManageTemplates />) },
            { path: 'manage-templates/edit/:type', loader: editTemplateLoader, element: suspend(<EditTemplate />) },
            { path: 'manage-templates/edit/:type/:id', loader: editTemplateLoader, element: suspend(<EditTemplate />) },
            { path: 'manage-charts', element: suspend(<ManageCharts />) },
            { path: 'customs-systems', element: suspend(<CustomsSystems />) },
            { path: 'manage-batches', element: suspend(<ManageBatches />) },
            { path: 'manage-transits', element: suspend(<ManageTransits />) },
            { path: 'manage-tsds', element: suspend(<ManageTSDs />) },
            { path: 'manage-customs-errors', element: suspend(<ManageCustomsErrors />) },
            { path: 'manage-catalogs', element: suspend(<ManageCatalogs />) },
            { path: 'manage-codes', element: suspend(<ManageCodes />) },
            { path: 'manage-codes/:family', element: suspend(<ManageCodesFamily />) },
            { path: 'request-observability', element: suspend(<RequestLogger />) },
            { path: 'hs-code-classifier', element: suspend(<HsCodeClassifier />) },
          ],
        },
        { path: 'auth', element: suspend(<AdminLogin />) },
        { path: '*', element: <Navigate to={base || '/'} replace /> },
      ],
    },
  ];
};
